import { ChatMessage, ContactInfo, TransMasterData } from "../types";
import { toast } from "react-hot-toast";

// Helper to extract job role from addDetails JSON
export const getJobRole = (addDetails: string | null, role?: string | null) => {
  if (!addDetails) return role || "-";
  try {
    const parsed = JSON.parse(addDetails);
    // Prioritize camelCase field names since backend now returns camelCase
    return parsed.jobTitle || parsed.title || parsed.name || role || "-";
  } catch {
    return role || "-";
  }
};


// Helper to extract initial outreach message from processDetails JSON
export const getInitialOutreachMessage = (processDetails: string | null, contactType: "email" | "phone") => {
  if (!processDetails) return null;

  try {
    const parsed = JSON.parse(processDetails);

    // Try both structures: initialOutreachMessage (newer) and channelMessage (older)
    const outreachMessage = parsed.initialOutreachMessage?.[0] || parsed.channelMessage?.[0];

    if (!outreachMessage) return null;

    if (contactType === "email") {
      // Handle both structures: nested email object or direct email property
      return outreachMessage.email?.emailMessage || outreachMessage.email || null;
    } else {
      return outreachMessage.im || null;
    }
  } catch {
    return null;
  }
};

export const downloadChatHistoryCSV = (
  messages: ChatMessage[],
  contactInfo: ContactInfo,
  transMasterData: TransMasterData | null
) => {
  if (messages.length === 0) {
    toast.error("No chat history to download");
    return;
  }

  // Prepare metadata
  const contactDisplay = contactInfo.email 
    ? `${contactInfo.email}` 
    : `${contactInfo.phone}${contactInfo.subChannel ? ` (${contactInfo.subChannel})` : ''}`;
    
  const metadata = [
    ["# Chat History Export"],
    [`# Generated on: ${new Date().toISOString()}`],
    [`# Contact: ${contactDisplay || "Unknown"}`],
    [`# JobNo: ${transMasterData?.externalCd || "N/A"}`],
    [`# Role: ${getJobRole(transMasterData?.addDetails || null)}`],
    [`# Agent Type: ${transMasterData?.agentType || "N/A"}`],
    ...(contactInfo.subChannel ? [[`# Sub Channel: ${contactInfo.subChannel}`]] : []),
    [`# Total Messages: ${messages.length}`],
    [""], // Empty row for separation
  ];

  // Prepare CSV data
  const csvHeaders = ["Timestamp", "Date", "Time", "Sender", "Message", "Message_Length", "UUID"];
  const csvRows = messages.map(message => {
    const timestamp = message.timestamp;
    return [
      timestamp.toISOString(),
      timestamp.toLocaleDateString(),
      timestamp.toLocaleTimeString(),
      message.isUser ? "User" : "AI Assistant",
      `"${message.text.replace(/"/g, '""')}"`, // Escape quotes in message text
      message.text.length.toString(),
      message.uuid || ""
    ];
  });

  // Create CSV content with metadata and data
  const csvContent = [
    ...metadata.map(row => row.join(",")),
    csvHeaders.join(","),
    ...csvRows.map(row => row.join(","))
  ].join("\n");

  // Create and download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    
    // Generate filename with timestamp and contact info
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const contactIdentifier = (contactInfo.email || contactInfo.phone || "unknown").replace(/[^a-zA-Z0-9]/g, "_");
    const filename = `chat-history-${contactIdentifier}-${timestamp}.csv`;
    
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    URL.revokeObjectURL(url);
    
    toast.success(`Chat history downloaded as ${filename}`);
  } else {
    toast.error("Download not supported in this browser");
  }
};

// Helper to prepare ServiceoApp specific data for message requests
export const prepareServiceoAppData = (
  transMasterData: TransMasterData | null,
  selectedCandidate: { recipientId: string } | null
) => {
  const serviceoAppData: { jobId?: string; workerId?: string } = {};
  
  if (transMasterData?.externalId) {
    serviceoAppData.jobId = transMasterData.externalId;
  }
  
  if (selectedCandidate?.recipientId) {
    serviceoAppData.workerId = selectedCandidate.recipientId;
  }
  
  return serviceoAppData;
};

// Helper to validate ServiceoApp data availability
export const validateServiceoAppData = (
  transMasterData: TransMasterData | null,
  selectedCandidate: { recipientId: string } | null
): { isValid: boolean; missingFields: string[] } => {
  const missingFields: string[] = [];
  
  if (!transMasterData?.externalId) {
    missingFields.push('jobId (externalId)');
  }
  
  if (!selectedCandidate?.recipientId) {
    missingFields.push('workerId (recipientId)');
  }
  
  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};